import { CommonModule, formatDate } from '@angular/common';
import { Component, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { NgIcon, provideIcons } from '@ng-icons/core';
import { hugeEvCharging, hugeFlash, hugeTimer02 } from '@ng-icons/huge-icons';

import { finalize, firstValueFrom, forkJoin, map, Observable, tap } from 'rxjs';

import { CardModule } from 'primeng/card';
import { DatePickerModule } from 'primeng/datepicker';
import { DividerModule } from 'primeng/divider';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule, TableRowSelectEvent } from 'primeng/table';
import { TagModule } from 'primeng/tag';

import {
  FilterChangeEvent,
  PageChangeEvent,
  PaginationMeta,
  QueryParams,
  SelectFilterOptionsRequest,
  SelectFilterOptionsResponse,
} from '../../core/models/catalog-table.model';

import {
  StationAP,
  StationAPResponse,
  StationSimple,
  StationSummary,
} from '../../core/models/station.model';

import { CompaniesService } from '../../core/services/companies.service';
import { StationsAPService } from '../../core/services/stations-availability-performance.service';
import { StationsService } from '../../core/services/stations.service';

import { CircularProgressComponent } from '../../shared/components/circular-progress/circular-progress.component';
import { TimeFormatPipe } from '../../shared/pipes/time-format.pipe';

/**
 * Dashboard de inicio: indicadores de disponibilidad y desempeño (energía, sesiones,
 * tiempo de uso) por estación, filtrables por empresa, estación y rango de fechas
 * (por defecto, del primer día del mes a hoy).
 *
 * Al seleccionar una estación de la tabla se cargan en paralelo el detalle por
 * cargador, el uso por tipo de conector y el uso por conector.
 */
@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgIcon,
    CardModule,
    DatePickerModule,
    DividerModule,
    PaginatorModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    CircularProgressComponent,
    TimeFormatPipe,
  ],
  viewProviders: [
    provideIcons({
      hugeEvCharging,
      hugeFlash,
      hugeTimer02,
    }),
  ],
  providers: [TimeFormatPipe],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent {
  private today = signal<Date>(new Date());

  private readonly firstDayOfMonth = computed(() => {
    const today = this.today();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });

  private readonly defaultRangeDate = computed<Date[]>(() => {
    return [this.firstDayOfMonth(), this.today()];
  });

  rangeDates = signal<Date[] | undefined>(undefined);

  companiesOpts: { label: string; value: number }[] = [];
  selectedCompany: any | undefined;

  stationsOpts: { label: string; value: number }[] = [];
  selectedStation: any | undefined;

  meta = signal<PaginationMeta>({
    page: 1,
    per_page: 10,
    total_items: 0,
    total_pages: 0,
  });

  protected currentParams: QueryParams = {
    page: 1,
    perPage: 10,
    filters: {},
    sortOrder: 1,
  };

  protected getDefaultParams(): QueryParams {
    return {
      page: 1,
      perPage: 10,
      filters: {},
      sortOrder: 1,
    };
  }

  // --- Disponibilidad y desempeño (listado general de estaciones) ---

  loadingStationAP = signal(false);

  availabilityPerformance = signal<StationAP | null>(null);

  summary = computed<StationSummary>(() => {
    const summary = { energy_kwh: 0, sessions_count: 0, usage_seconds: 0 };
    return this.availabilityPerformance()?.summary || summary;
  });

  stations = computed<StationSimple[]>(() => {
    return this.availabilityPerformance()?.stations || [];
  });

  station = signal<StationSimple | null>(null);

  stationName = computed(() => {
    return this.station()?.name || 'Estación sin nombre';
  });

  // --- Detalle de la estación seleccionada (cargadores, tipos de conector y conectores) ---

  loadingAllData = signal(false);

  chargers = signal<any[]>([]);
  connectorTypes = signal<any[]>([]);
  connectors = signal<any[]>([]);

  constructor(
    private companiesService: CompaniesService,
    private stationsAPService: StationsAPService,
    private stationsService: StationsService,
  ) {
    this.loadCompanies();
    this.loadStations();
  }

  ngOnInit(): void {
    this.rangeDates.set(this.defaultRangeDate());

    const [startDate, endDate] = this.defaultRangeDate();
    const fromDate = formatDate(startDate, 'yyyy-MM-dd', 'en-US');
    const toDate = formatDate(endDate, 'yyyy-MM-dd', 'en-US');

    const currentFilters = this.currentParams.filters;
    this.currentParams.filters = {
      ...currentFilters,
      from_date: fromDate,
      to_date: toDate,
    };

    this.loadData();
  }

  async loadCompanies() {
    const request: SelectFilterOptionsRequest = { page: 1, size: 20 };
    const response: any = await this.loadCompaniesFilterPage(request);
    this.companiesOpts = response.options;
  }

  async loadStations(companyId?: number) {
    const request: SelectFilterOptionsRequest = { page: 1, size: 20 };

    if (companyId) {
      request['company_id'] = companyId;
    }

    const response: any = await this.loadStationsFilterPage(request);
    this.stationsOpts = response.options;
  }

  private async loadCompaniesFilterPage(
    request: SelectFilterOptionsRequest,
  ): Promise<SelectFilterOptionsResponse> {
    const filters = request.search ? { name: request.search } : {};
    const response = await firstValueFrom(
      this.companiesService.getAll(request.page, request.size, filters, 'name', 1),
    );

    return {
      options: response.data.map((company) => ({
        label: company.name,
        value: company.id,
      })),
      hasMore: request.page < response.meta.total_pages,
    };
  }

  private async loadStationsFilterPage(
    request: SelectFilterOptionsRequest,
  ): Promise<SelectFilterOptionsResponse> {
    const filters: { [key: string]: any } = {};

    if (request.search) {
      filters['name'] = request.search;
    }

    if (request.company_id) {
      filters['company_id'] = request.company_id;
    }

    const response = await firstValueFrom(
      this.stationsService.getAll(request.page, request.size, filters, 'name', 1),
    );

    return {
      options: response.data.map((station) => ({
        label: station.name,
        value: station.id,
      })),
      hasMore: request.page < response.meta.total_pages,
    };
  }

  private loadData(): void {
    this.loadingStationAP.set(true);
    this.stationsAPService
      .getAvailabilityPerformance(
        this.currentParams.page,
        this.currentParams.perPage,
        this.currentParams.filters,
      )
      .subscribe({
        next: (response: StationAPResponse) => {
          if (response && response.data) {
            this.meta.set(response.meta);
            this.availabilityPerformance.set(response.data);
          }
        },
        error: (error) => {
          console.error('Error loading stations analytics:', error);
          this.loadingStationAP.set(false);
        },
        complete: () => this.loadingStationAP.set(false)
      });
  }

  handlerOnRowSelect(event: TableRowSelectEvent): void {
    if (!event || !event.data?.id) {
      console.error('Error loading station summary:');
      return;
    }

    this.chargers.set([]);
    this.connectorTypes.set([]);
    this.connectors.set([]);
    this.loadingAllData.set(true);

    this.loadAllData(event.data.id)
      .pipe(
        finalize(() => {
          this.loadingAllData.set(false);
        }),
      )
      .subscribe({
        error: (error: any) => console.error(error),
      });
  }

  private loadAllData(stationId: number): Observable<any> {
    const filters = this.currentParams.filters;

    const loaders: Observable<any>[] = [
      this.loadAvailabilityPerformance(stationId, filters),
      this.loadConnectorTypeUsage(stationId, filters),
      this.loadConnectorUsage(stationId, filters),
    ];

    return forkJoin(loaders);
  }

  private loadAvailabilityPerformance(
    id: number,
    params: any,
  ): Observable<any> {
    return this.stationsAPService
      .getAvailabilityPerformanceById(id, params)
      .pipe(
        map((resp: any) => {
          const data = resp?.data || resp || {};
          return data?.chargers || [];
        }),
        tap((chargers) => {
          this.chargers.set(chargers);
        }),
      );
  }

  private loadConnectorTypeUsage(id: number, params: any): Observable<any> {
    return this.stationsAPService.getConnectorTypeUsageById(id, params).pipe(
      map((resp: any) => {
        const data = resp?.data || resp || {};
        return data?.connector_types || [];
      }),
      tap((connectorTypes) => {
        this.connectorTypes.set(connectorTypes);
      }),
    );
  }

  private loadConnectorUsage(id: number, params: any): Observable<any> {
    return this.stationsAPService.getConnectorUsageById(id, params).pipe(
      map((resp: any) => {
        const data = resp?.data || resp || {};
        return data?.connectors || [];
      }),
      tap((connectors) => {
        this.connectors.set(connectors);
      }),
    );
  }

  handlePageChange(event: PaginatorState) {
    this.onPageChange({
      page: (event.page || 0) + 1,
      rows: event.rows || 10,
    });
  }

  private onPageChange(event: PageChangeEvent): void {
    this.currentParams.page = event.page || 0;
    this.currentParams.perPage = event.rows || 10;
    this.station.set(null);
    this.loadData();
  }

  handleFilterChange(field: string, value: any) {
    const updatedFilters = { ...this.currentParams.filters };

    if (field === 'range_dates') {
      const isRangeComplete =
        Array.isArray(value) && value.length === 2 && value[0] && value[1];

      if (isRangeComplete) {
        const [startDate, endDate] = this.getValidDates(value);

        this.rangeDates.set([startDate, endDate]);
        updatedFilters['from_date'] = formatDate(
          startDate,
          'yyyy-MM-dd',
          'en-US',
        );
        updatedFilters['to_date'] = formatDate(endDate, 'yyyy-MM-dd', 'en-US');

        this.onFilterChange({ field, value, filters: updatedFilters });
      }
      return;
    }

    if (value === null || value === undefined || value === '') {
      delete updatedFilters[field];
    } else {
      updatedFilters[field] = value;
    }

    const filterChangeEvent: FilterChangeEvent = {
      field: field,
      value: value,
      filters: updatedFilters,
    };

    this.onFilterChange(filterChangeEvent);
  }

  private onFilterChange(event: FilterChangeEvent): void {
    // Aplicar filtros y volver a la primera página
    this.currentParams.filters = event.filters;
    this.currentParams.page = 1;

    // Limpiar la selección y los datos dependientes de la estación anterior
    this.station.set(null);
    this.chargers.set([]);
    this.connectorTypes.set([]);
    this.connectors.set([]);

    this.loadData();
  }

  /** Devuelve el rango seleccionado; si es inválido o ambas fechas son el mismo día, usa el rango por defecto. */
  private getValidDates(value: any): Date[] {
    const isValidArray =
      Array.isArray(value) && value.length === 2 && value[0] && value[1];

    if (isValidArray) {
      const [startDate, endDate] = value;
      const isSameDate = startDate.toDateString() === endDate.toDateString();

      if (!isSameDate) {
        return [startDate, endDate];
      }
    }

    return this.defaultRangeDate();
  }
}
